'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import { useState } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { TDynamicData } from '../../types/dynamic-data';
import type { Service } from '../../types/services';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { PriceOfferButton } from '../price-offer/price-offer-button';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { LuArrowUpRight } from 'react-icons/lu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { HtmlDescription } from '../shared/html-description';

export function WeRealize({ service, allPoints }: { service: Service; allPoints: TDynamicData[] }) {
  const points = allPoints.filter(point =>
    point.fields?.some(field => field.values?.some(value => value.name === service.name)),
  );

  const [expanded, setExpanded] = useState<string>(points[0]?.slug);

  return (
    <section className={cn('custom-section', 'py-20 sm:py-32 lg:py-40')}>
      <div
        className={cn(
          'custom-container--sm',
          'flex flex-col items-start justify-start gap-10 md:flex-row md:items-center md:justify-between lg:gap-16 xl:gap-20',
        )}
      >
        <div className="relative h-[400px] w-full overflow-hidden rounded-[12px] sm:h-[550px] sm:max-w-[584px] sm:rounded-[16px] lg:h-[650px] lg:rounded-[32px] xl:h-[800px]">
          <Image
            src={service?.image?.url ?? '/images/placeholder.jpg'}
            alt={resolveImageAlt(service?.image, service?.name)}
            height={0}
            width={0}
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
            sizes="100vw"
          />
          <ResponsiveDialogProvider>
            <PriceOfferButton
              title="Chcem získať cenovú ponuku na projekt stavby"
              variant={'default'}
              icon={
                <LuArrowUpRight
                  size={20}
                  className="min-h-10 min-w-10 sm:min-h-12 sm:min-w-12 lg:min-h-14 lg:min-w-14"
                />
              }
              className="absolute bottom-4 left-4 right-4 z-10 max-h-[126px] text-left text-xl font-normal sm:bottom-6 sm:left-6 sm:right-6 sm:text-2xl lg:text-3xl"
            />
          </ResponsiveDialogProvider>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[584px] lg:gap-8">
          <p className="text-sm font-normal text-black sm:text-base">Zrealizujeme</p>
          <Accordion
            defaultChecked={true}
            defaultValue={expanded}
            onValueChange={value => setExpanded(value)}
            type="single"
            className={cn(
              'col-span-1 flex w-full flex-col items-start justify-start gap-6 sm:gap-8',
              'ea-collaboration__accordion',
            )}
          >
            {points.map(point => (
              <AccordionItem
                key={point.slug}
                value={point.slug}
                className={cn('w-full border-none', 'ea-collaboration__item')}
              >
                <AccordionTrigger
                  className={cn(
                    'flex w-full items-center justify-start gap-3 text-left sm:gap-4 lg:gap-6',
                    'ea-collaboration__trigger',
                  )}
                >
                  {point.slug === expanded && (
                    <div className="aspect-square min-w-4 rounded-full bg-primary sm:min-w-5" />
                  )}

                  {point.name}
                </AccordionTrigger>
                <AccordionContent className={cn('w-full font-light', 'ea-collaboration__content')}>
                  <HtmlDescription
                    content={point.fields[1]?.value}
                    size="sm"
                    className="w-full pt-0 text-black/70 sm:pt-2 lg:pt-4"
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
